
import { Vue, Options } from "vue-class-component";
import { SttHangingController } from "@/app/ui/controllers/SttHangingController";
import {
  CLIENT_ERROR,
  INTERNET_ERROR,
  INTERNAL_SERVER_ERROR
} from "@/app/infrastructures/misc/Utils";
import {
  HangingLastMile,
  HangingLastMileSummary
} from "@/domain/entities/HangingLastMile";
import Header from "./components/header.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  HangingLastMileConfirmApiRequest,
  SubmitHangingConfirmationUpdateStatus
} from "@/data/payload/api/HangingApiRequest";
import { HangingLastMileFilterRequest } from "@/domain/entities/HangingIncoming";
import CardCollapsible from "./components/card-collapsible.vue";
import Filter from "@/app/ui/views/dashboard/component/console/hanging/tab-hanging-incoming/components/filter.vue";
import { debounce } from "lodash";
import Actions from "@/app/ui/views/dashboard/component/console/hanging/tab-hanging-incoming/components/actions.vue";
import CardProgress from "@/app/ui/views/dashboard/component/console/hanging/tab-hanging-incoming/components/card-progress.vue";
import ConfirmAction from "@/app/ui/views/dashboard/component/console/hanging/components/ConfirmAction.vue";
import CopyTemplate from "@/app/ui/views/dashboard/component/console/hanging/components/CopyTemplate.vue";
import { AccountController } from "@/app/ui/controllers/AccountController";

@Options({
  components: {
    Header,
    CardCollapsible,
    Filter,
    Actions,
    CardProgress,
    ConfirmAction,
    CopyTemplate
  }
})
class TabLastmile extends Vue {
  isLoadingFetch = false;
  errorState = "";
  dataSummary = new HangingLastMileSummary();
  hangingLastMileList: HangingLastMile[] = [];
  isLoadingList = false;

  get accountData() {
    return AccountController.accountData;
  }

  async getHangingLastMileSummary() {
    try {
      this.isLoadingFetch = true;
      this.errorState = "";
      const res = await SttHangingController.getHangingLastMileSummary();
      this.dataSummary = res;
    } catch (error) {
      this._handlerError(error);
    } finally {
      this.isLoadingFetch = false;
    }
  }

  _handlerError(error: any) {
    if (!error.response) {
      this.errorState = INTERNET_ERROR;
      return;
    }
    if (error.response.status > 500) {
      this.errorState = CLIENT_ERROR;
      return;
    }
    this.errorState = INTERNAL_SERVER_ERROR;
  }

  async mounted() {
    await this.getHangingLastMileSummary();
    await this.getHangingLastMileList(new HangingLastMileFilterRequest());
  }

  // handling the modals

  messageHtml = "";

  followUpData = new HangingLastMileConfirmApiRequest();

  statusUpdateData: SubmitHangingConfirmationUpdateStatus = new SubmitHangingConfirmationUpdateStatus(
    {
      customProcessStatus: "MISSING",
      sttNo: ["88LP1758092401560", "99LP1758006313265"],
      remarks: "Hanging",
      partnerId: 11325
    }
  );

  // hanging last mile
  async getHangingLastMileList(params: HangingLastMileFilterRequest) {
    try {
      this.isLoadingList = true;
      const res = await SttHangingController.getHangingLastMile(params);
      this.hangingLastMileList = res;
    } catch (err) {
      this.hangingLastMileList = [];
    } finally {
      this.isLoadingList = false;
    }
  }

  isOpenTemplate = false;
  selectedCard = {
    stt: [] as string[],
    groupName: ""
  };

  onClickTemplateMessage(item: HangingLastMile) {
    this.selectedCard = {
      stt: item.groups.flatMap((group) => group.stts).map((stt) => stt.sttNo),
      groupName: item.groups.flatMap((group) => group.groupName).join(",")
    };

    this.isOpenTemplate = true;
  }

  filter = new HangingLastMileFilterRequest();
  get isEmptyList() {
    const isEmptyFilter = Object.values(this.filter).every(
      (value) => value === "" || value == null
    );
    return (
      !this.hangingLastMileList.length && !this.isLoadingList && isEmptyFilter
    );
  }

  getFilter = debounce((filter: any) => {
    const params = new HangingLastMileFilterRequest({
      existenceStatus: filter.existenceStatus,
      days: filter.days,
      search: filter.search,
      statusFollowUp: filter.statusFollowUp
    });
    this.filter = params;
    this.getHangingLastMileList(this.filter);
  }, 250);

  openConfirmUpdateStatus = false;
  lastStatus = "";

  get allSelectedStt(): string[] {
    return this.hangingLastMileList
      .flatMap((item) => item.groups)
      .flatMap((group) => group.stts)
      .filter((stt) => stt.isChecked)
      .map((stt) => stt.sttNo);
  }

  onConfirmUpdateStatus(status: string) {
    this.lastStatus = status;
    const statusUppercase = status.toUpperCase();
    const statusUpdate =
      statusUppercase === "NOT RECEIVED" ? "NOT_RECEIVED" : statusUppercase;

    this.messageHtml = this.$t("dashboard.hanging.confirmUpdateMessage", { status });

    this.statusUpdateData = new SubmitHangingConfirmationUpdateStatus({
      customProcessStatus: statusUpdate,
      sttNo: this.allSelectedStt,
      remarks: "Hanging",
      pageType: "hanging"
    });
    this.openConfirmUpdateStatus = true;
  }

  async onUpdateStatus() {
    try {
      this.openConfirmUpdateStatus = false;
      MainAppController.closeErrorMessage();
      MainAppController.showLoading();

      const resp = await SttHangingController.submitHangingConfirmationUpdateStatus(
        this.statusUpdateData
      );
      const message = resp.totalSttFailed
        ? `<div class="text-base">${this.$t("dashboard.hanging.successUpdateStatusWithRatio", { status: this.lastStatus, success: resp.totalSttSuccess, total: resp.totalSttSuccess + resp.totalSttFailed })}</div>`
        : `<div class="text-base">${this.$t("dashboard.hanging.successUpdateStatus", { status: this.lastStatus })}</div>`;
      const color = resp.totalSttFailed ? '#FAB713' : '#36B37E';

      MainAppController.setSnackbarMessage(message);
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(5000);
      MainAppController.setSnackbarBackgroundColor(color);
      MainAppController.setSnackbar(true);

      this.refreshList();
    } catch (err) {
      MainAppController.setSnackbarMessage(
        this.$t("dashboard.hanging.failUpdateStatus", { status: this.lastStatus })
      );
      MainAppController.setSnackbarTimeout(5000);
      MainAppController.setSnackbarPositivity(false);
      MainAppController.setSnackbar(true);
    } finally {
      MainAppController.closeLoading();
      this.followUpData = new HangingLastMileConfirmApiRequest();
    }
  }

  refreshList() {
    this.getHangingLastMileSummary();
    this.getHangingLastMileList(this.filter);
  }

  downloadHangingLastMile(listStt: []) {
    SttHangingController.downloadHangingLastMile(listStt);
  }
}
export default TabLastmile;
